import { HHButton, HHStack } from '@hinge-health/react-component-library';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  BillingType,
  ClientType,
  InsurerType,
  PartnershipType,
} from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import {
  BillingTypeFilter,
  CiStatusFilter,
  ClientFilter,
  ContractStatusFilter,
  InsurerFilter,
  PartnershipFilter,
} from '../form';
import {
  CiStatusFilter as CiStatusFilterEnum,
  ClientInsurerIndexFilterModel,
  ContractStatusFilter as ContractStatusFilterEnum,
  defaultClientInsurerIndexFilters,
  RawClientInsurerIndexFilterValues,
} from './client-insurer-index-types';
import { PaginationModel } from './use-client-insurer-index-queries';

interface ClientInsurerIndexFilterBarProps {
  filterParams: ClientInsurerIndexFilterModel;
  setFilterParams: Dispatch<SetStateAction<ClientInsurerIndexFilterModel>>;
  setPaginationModel: Dispatch<SetStateAction<PaginationModel>>;
  clients: ClientType[];
  clientsError?: boolean;
  insurers: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  insurersError?: boolean;
  partnerships: PartnershipType[];
  partnershipsError?: boolean;
  billingTypes: BillingType[];
  loading?: boolean;
}

export const ClientInsurerIndexFilterBar = ({
  filterParams,
  setFilterParams,
  setPaginationModel,
  clients,
  clientsError,
  insurers,
  insurersError,
  partnerships,
  partnershipsError,
  billingTypes,
  loading = false,
}: ClientInsurerIndexFilterBarProps): JSX.Element => {
  const defaultValues: RawClientInsurerIndexFilterValues = {
    clients: [],
    insurers: [],
    partnerships: [],
    billingTypes: [],
    ciStatus: CiStatusFilterEnum.All,
    contractStatus: ContractStatusFilterEnum.All,
  };

  const { handleSubmit, control, formState, reset } =
    useForm<RawClientInsurerIndexFilterValues>({
      mode: 'onChange',
      defaultValues,
    });

  const { isDirty, errors } = formState;

  useEffect(() => {
    reset({
      clients: clients.filter(c => filterParams.clientIds.includes(c.id)),
      insurers: insurers.filter(i => filterParams.insurerIds.includes(i.id)),
      partnerships: partnerships.filter(p =>
        filterParams.partnershipIds.includes(p.id),
      ),
      billingTypes: billingTypes
        .filter(bt => filterParams.billingTypes.includes(bt.value))
        .map(bt => ({
          value: bt.value,
          label: capitalizeFirstLetterAndSpaceSnakeString(bt.value),
        })),
      ciStatus: filterParams.ciStatus,
      contractStatus: filterParams.contractStatus,
    });
  }, [filterParams, reset, clients, insurers, partnerships, billingTypes]);

  const onApplyClick = (): void => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    handleSubmit(data => {
      const formatted: ClientInsurerIndexFilterModel = {
        clientIds: data.clients.map(c => c.id),
        insurerIds: data.insurers.map(i => i.id),
        partnershipIds: data.partnerships.map(p => p.id),
        billingTypes: data.billingTypes.map(bt => bt.value),
        ciStatus: data.ciStatus,
        contractStatus: data.contractStatus,
      };

      setFilterParams(formatted);
      reset(data);
    })();
  };

  const handleReset = (): void => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    reset(defaultValues);
    setFilterParams(defaultClientInsurerIndexFilters);
  };

  const hasError = !!clientsError || !!insurersError || !!partnershipsError;

  const isFiltersDefault =
    filterParams.clientIds.length === 0 &&
    filterParams.insurerIds.length === 0 &&
    filterParams.partnershipIds.length === 0 &&
    filterParams.billingTypes.length === 0 &&
    filterParams.ciStatus === CiStatusFilterEnum.All &&
    filterParams.contractStatus === ContractStatusFilterEnum.All;

  return (
    <HHStack
      direction="row"
      flexWrap="wrap"
      alignItems="flex-end"
      paddingX={6}
      sx={{ gap: 2 }}
    >
      <ClientFilter
        control={control}
        errors={errors}
        clients={clients}
        hasError={!!clientsError}
        loading={loading}
      />
      <InsurerFilter
        control={control}
        errors={errors}
        insurers={insurers}
        hasError={!!insurersError}
        loading={loading}
      />
      <PartnershipFilter
        control={control}
        errors={errors}
        partnerships={partnerships}
        hasError={partnershipsError}
        loading={loading}
        label={'Contract Partnership'}
      />
      <BillingTypeFilter
        control={control}
        errors={errors}
        billingTypes={billingTypes}
        loading={loading}
      />
      <CiStatusFilter control={control} errors={errors} />
      <ContractStatusFilter control={control} errors={errors} />

      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ marginLeft: 'auto', flexShrink: 0 }}
      >
        <HHButton
          size="medium"
          hhVariant="contained"
          onClick={onApplyClick}
          disabled={!isDirty || hasError}
        >
          Apply
        </HHButton>
        <HHButton
          size="medium"
          hhVariant="outlined"
          onClick={handleReset}
          disabled={(isFiltersDefault && !isDirty) || loading}
        >
          Reset
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
